import React, { useState, useRef, useEffect } from 'react'
import Imgix from 'react-imgix'
import { UploadIcon } from '../assetsComponents'
import classNames from 'classnames'
import API from '../../utils/API'
import axios from 'axios'
import {
  useErrorService,
  useNotificationService,
  useUserService,
} from '@abroad/components'
import ImagePlacholder from '../assetsComponents/ImagePlacholder'
import UnsavedDocumentModal from './UnsavedDocumentModal'
import { gtmUploadExperiencePassportAction } from './gtmQuestUploadFunctions'

const UploadPassport = ({ questId }) => {
  const { user } = useUserService()
  const [uploadedPassportDocs, setUploadedPassportDocs] = useState([])
  const [isLoadingPage, setIsLoadingPage] = useState(true)
  const [passportData, setPassportData] = useState()
  const [selectedFiles, setSelectedFiles] = useState([]) // [{ file, previewUrl, progress, status, fileNameFromSignedUrl }]
  const [dragActive, setDragActive] = useState(false)
  const inputRef = useRef(null)
  const { showError } = useErrorService()
  const Notification = useNotificationService()
  const userId = user?.id
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [pendingDeleteFile, setPendingDeleteFile] = useState('')

  // Fetch passport data on mount
  useEffect(() => {
    const getQuestPassportData = async () => {
      if (userId && questId) {
        try {
          const { data } = await API.quests.getTravelDocument(questId, userId)
          setPassportData(data)
          if (
            data &&
            data?.travelDocuments &&
            data?.travelDocuments?.length > 0
          ) {
            const passportDocs = data?.travelDocuments?.filter(
              (doc) => doc.documentType === 'passport',
            )
            setUploadedPassportDocs(passportDocs || [])
          } else {
            setUploadedPassportDocs([])
          }
        } catch (error) {
          showError(error)
          setUploadedPassportDocs([])
        } finally {
          setIsLoadingPage(false)
        }
      } else {
        setIsLoadingPage(false)
      }
    }
    if (user !== undefined) {
      getQuestPassportData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId, userId])

  // Fetch preview URLs for all uploaded passport files
  const [uploadedPreviews, setUploadedPreviews] = useState({}) // { fileName: url }
  useEffect(() => {
    const fetchAllPreviews = async () => {
      if (!uploadedPassportDocs.length || !userId || !questId) return
      let previews = {}
      for (const doc of uploadedPassportDocs) {
        for (const fileName of doc.fileName) {
          try {
            const { data } = await API.quests.downloadTravelDocument(
              questId,
              userId,
              fileName,
            )
            if (data && data.url) {
              previews[fileName] = data.url
            }
          } catch (e) {
            showError(e)
          }
        }
      }
      setUploadedPreviews(previews)
    }
    fetchAllPreviews()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedPassportDocs, userId, questId])

  // Handle file selection (by input or drag)
  const handleFiles = (files) => {
    const validFiles = Array.from(files).filter(
      (file) =>
        file.type === 'image/jpeg' ||
        file.type === 'image/png' ||
        file.type === 'application/pdf',
    )
    if (validFiles.length !== files.length) {
      Notification.showNotification('Please upload only JPG, PNG, or PDF files')
    }
    const newFileObjs = validFiles.map((file) => ({
      file,
      previewUrl: file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : '',
      progress: 0,
      status: 'pending',
      fileNameFromSignedUrl: '',
    }))
    setSelectedFiles((prev) => [...prev, ...newFileObjs])
  }

  // Start upload for new files
  useEffect(() => {
    selectedFiles.forEach((fileObj, idx) => {
      if (fileObj.status === 'pending') {
        uploadPassportToSignedUrl(fileObj, idx)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles])

  // Upload logic for each file
  const uploadPassportToSignedUrl = async (fileObj, idx) => {
    const file = fileObj.file
    if (!file) {
      Notification.showNotification('Please select a file to upload')
      return
    }
    setSelectedFiles((prev) =>
      prev.map((f, i) =>
        i === idx ? { ...f, status: 'uploading', progress: 0 } : f,
      ),
    )
    try {
      const contentType = file.name
        .substr(file.name.lastIndexOf('.') + 1)
        .toUpperCase()
      const documentType = 'passport'
      const { data: signedUrlData } =
        await API.quests.getTravelDocumentSignedURL(
          questId,
          contentType,
          documentType,
        )
      if (signedUrlData && signedUrlData.url) {
        await axios.put(signedUrlData.url.url, file, {
          headers: {
            'Content-Type': signedUrlData.url.contentType,
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 95) / progressEvent.total,
              )
              setSelectedFiles((prev) =>
                prev.map((f, i) =>
                  i === idx ? { ...f, progress: percent } : f,
                ),
              )
            }
          },
        })
        // Finalize upload
        if (userId) {
          const { data: uploadApiResult } =
            await API.quests.uploadTravelDocument(questId, userId, {
              fileName: signedUrlData.fileName,
              documentType: 'passport',
            })
          if (uploadApiResult && uploadApiResult?.travelDocuments) {
            const passportDocs = uploadApiResult?.travelDocuments?.filter(
              (doc) => doc.documentType === 'passport',
            )
            setUploadedPassportDocs(passportDocs || [])

            // Track GTM event for successful upload
            gtmUploadExperiencePassportAction(
              questId,
              signedUrlData.fileName,
              file.type,
              userId,
            )
          }
          // Remove the file from selectedFiles after upload
          setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))
        } else {
          throw new Error('User session error. Please try logging in again.')
        }
      } else {
        throw new Error('Could not get signed URL.')
      }
    } catch (e) {
      Notification.showNotification('Upload failed. Please try again.')
      setSelectedFiles((prev) =>
        prev.map((f, i) => (i === idx ? { ...f, status: 'error' } : f)),
      )
    }
  }

  // Remove file from selectedFiles
  const handleRemovePendingFile = (idx) => {
    setSelectedFiles((prev) => {
      // Revoke object URL to avoid memory leaks
      if (prev[idx]?.previewUrl) URL.revokeObjectURL(prev[idx].previewUrl)
      return prev.filter((_, i) => i !== idx)
    })
  }

  // Delete uploaded image
  const handleDelete = async (fileName) => {
    const documentType = 'passport'
    try {
      await API.quests.deleteTravelDocument(
        questId,
        userId,
        fileName,
        documentType,
      )
      setUploadedPassportDocs((currentDocs) => {
        const updatedDocs = currentDocs
          .map((doc) => ({
            ...doc,
            fileName: doc.fileName.filter((f) => f !== fileName),
          }))
          .filter((doc) => doc.fileName.length > 0)
        return updatedDocs
      })
    } catch (error) {
      showError(error)
    }
  }

  const handleDeleteIconClick = (fileName) => {
    setPendingDeleteFile(fileName)
    setShowDeleteModal(true)
  }
  const handleDeleteConfirm = async () => {
    if (pendingDeleteFile) {
      await handleDelete(pendingDeleteFile)
      setPendingDeleteFile('')
      setShowDeleteModal(false)
    }
  }
  const handleDeleteCancel = () => {
    setPendingDeleteFile('')
    setShowDeleteModal(false)
  }

  const onButtonClick = () => {
    inputRef.current.click()
  }

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }
  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }

  // Input change handler
  const handleChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files)
    }
  }

  // Add handler to download and open PDF in new tab
  const handlePdfClick = async (fileName) => {
    if (!questId || !userId || !fileName) return
    try {
      const { data } = await API.quests.downloadTravelDocument(
        questId,
        userId,
        fileName,
      )
      if (data && data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer')
      } else {
        showError({ message: 'Could not get download URL.' })
      }
    } catch (e) {
      showError(e)
    }
  }

  const processHtmlContent = (htmlContent) => {
    if (!htmlContent) return ''

    // Replace <strong> tags with <strong> having s6 class
    let processedContent = htmlContent.replace(
      /<strong>/g,
      '<strong class="s6">',
    )

    // Wrap the entire content in a div with s8 class for non-strong elements
    processedContent = `<div class="s8 mb-3">${processedContent}</div>`

    return processedContent
  }

  return (
    <div>
      {isLoadingPage ? (
        <div className='text-center mt-40px'>
          <div className='spinner-border text-saffron-700' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className='d-flex justify-content-center align-items-center mt-4'>
            {passportData?.stepConfiguration?.step3?.subStep3?.description && (
              <div
                className='s8 text-start'
                dangerouslySetInnerHTML={{
                  __html: processHtmlContent(
                    passportData.stepConfiguration.step3.subStep3.description,
                  ),
                }}
              />
            )}
          </div>
          <div className='d-flex justify-content-center'>
            <Imgix
              src='https://abroad-dev-new.imgix.net/passport_placeholder.png'
              alt='passport img'
              htmlAttributes={{
                style: {
                  width: '80%',
                  height: 'auto',
                  aspectRatio: 1.54,
                  objectFit: 'contain',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                },
              }}
            />
          </div>

          {/* Unified grid for uploaded and uploading files */}
          <div className='row mt-5 mb-3'>
            {/* Build a unified list of slots: uploading first (oldest first), then uploaded (oldest first) */}
            {[
              // Uploaded files (from server), sorted by timestamp in file name (oldest first)
              ...uploadedPassportDocs
                .flatMap((doc) =>
                  doc.fileName.map((fileName) => ({ fileName, doc })),
                )
                .sort((a, b) => {
                  // Extract timestamp from file name: passport-<timestamp>.<ext>
                  const getTs = (f) => {
                    const match = f.fileName.match(/passport-(\d+)\./)
                    return match ? parseInt(match[1], 10) : 0
                  }
                  return getTs(a) - getTs(b)
                })
                .map((item, idx) => ({
                  type: 'uploaded',
                  fileName: item.fileName,
                  doc: item.doc,
                  idx,
                })),
              // Uploading files (pending, uploading, or error) - oldest first
              ...selectedFiles.map((fileObj, idx) => ({
                type: 'uploading',
                fileObj,
                idx,
              })),
            ].map((slot, i) => {
              if (slot.type === 'uploading') {
                const { fileObj, idx } = slot
                return (
                  <div
                    key={'uploading-' + fileObj.file.name + idx}
                    className='col-xl-3 col-lg-4 col-md-4 col-12 mb-3'>
                    <div
                      className='p-2 bg-gray-100 border-radius-5 position-relative pb-3'
                      style={{
                        height: '322px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                      }}>
                      {/* Show preview if image, else placeholder for PDF */}
                      {fileObj.file.type === 'application/pdf' ? (
                        <div
                          style={{
                            width: '100%',
                            height: '240px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f8f9fa',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                          }}>
                          <svg
                            width='60'
                            height='60'
                            viewBox='0 0 20 20'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'>
                            <g clip-path='url(#clip0_16599_21131)'>
                              <path
                                d='M4.66667 10.6673H4V9.33398H4.66667C5.03485 9.33398 5.33333 9.63246 5.33333 10.0007C5.33333 10.3688 5.03485 10.6673 4.66667 10.6673Z'
                                fill='currentColor'
                              />
                              <path
                                d='M9.33203 13.334V9.33398H9.9987C10.3669 9.33398 10.6654 9.63246 10.6654 10.0007V12.6673C10.6654 13.0355 10.3669 13.334 9.9987 13.334H9.33203Z'
                                fill='currentColor'
                              />
                              <path
                                fill-rule='evenodd'
                                clip-rule='evenodd'
                                d='M1.33203 2C1.33203 0.895431 2.22746 0 3.33203 0H14.2748L18.6654 4.39052V18C18.6654 19.1045 17.7699 20 16.6654 20H3.33203C2.22746 20 1.33203 19.1045 1.33203 18V2ZM4.66536 8H2.66536V14.6667H3.9987V12H4.66536C5.76994 12 6.66536 11.1046 6.66536 10C6.66536 8.89543 5.76994 8 4.66536 8ZM9.9987 8H7.9987V14.6667H9.9987C11.1033 14.6667 11.9987 13.7712 11.9987 12.6667V10C11.9987 8.89543 11.1033 8 9.9987 8ZM13.332 14.6667V8H17.332V9.33333H14.6654V10.6667H15.9987V12H14.6654V14.6667H13.332Z'
                                fill='currentColor'
                              />
                            </g>
                            <defs>
                              <clipPath id='clip0_16599_21131'>
                                <rect width='20' height='20' fill='white' />
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      ) : fileObj.previewUrl ? (
                        <img
                          className='border-radius-5'
                          src={fileObj.previewUrl}
                          alt='passport img preview'
                          style={{
                            width: '100%',
                            height: '240px',
                            aspectRatio: '1.54',
                            objectFit: 'cover',
                            objectPosition: 'center top',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#f8f9fa',
                          }}
                        />
                      ) : (
                        <ImagePlacholder />
                      )}
                      {/* Progress bar while uploading */}
                      {(fileObj.status === 'uploading' ||
                        fileObj.status === 'pending') && (
                        <div style={{ width: '100%', marginTop: '16px' }}>
                          <div
                            style={{
                              width: '100%',
                              height: '8px',
                              background: '#FFF',
                              borderRadius: '50px',
                            }}>
                            <div
                              style={{
                                width: `${fileObj.progress}%`,
                                height: '100%',
                                background: '#EFC030',
                                transition: 'width 0.2s',
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {/* Error state */}
                      {fileObj.status === 'error' && (
                        <div className='text-danger mt-2'>Failed</div>
                      )}
                      <div
                        className='position-absolute top-12px right-12px'
                        onClick={() => handleRemovePendingFile(idx)}
                        style={{ cursor: 'pointer' }}>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='24'
                          height='24'
                          viewBox='0 0 24 24'
                          fill='none'>
                          <rect
                            x='24'
                            y='24'
                            width='24'
                            height='24'
                            rx='12'
                            transform='rotate(-180 24 24)'
                            fill='#EBEBEB'
                          />
                          <path
                            d='M17 7L7 17'
                            stroke='black'
                            strokeLinecap='round'
                          />
                          <path
                            d='M17 17L7 7'
                            stroke='black'
                            strokeLinecap='round'
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                )
              } else if (slot.type === 'uploaded') {
                const { fileName } = slot
                return (
                  <div
                    key={'uploaded-' + fileName}
                    className='col-xl-3 col-lg-4 col-md-4 col-12 mb-3'>
                    <div
                      className='p-2 bg-gray-100 border-radius-5 position-relative pb-3'
                      style={{
                        height: '322px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                      }}>
                      {fileName.endsWith('.pdf') ? (
                        <div
                          style={{
                            width: '100%',
                            height: '240px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f8f9fa',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                          onClick={() => handlePdfClick(fileName)}
                          title='Click to view PDF in new tab'>
                          <svg
                            width='60'
                            height='60'
                            viewBox='0 0 20 20'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'>
                            <g clip-path='url(#clip0_16599_21131)'>
                              <path
                                d='M4.66667 10.6673H4V9.33398H4.66667C5.03485 9.33398 5.33333 9.63246 5.33333 10.0007C5.33333 10.3688 5.03485 10.6673 4.66667 10.6673Z'
                                fill='currentColor'
                              />
                              <path
                                d='M9.33203 13.334V9.33398H9.9987C10.3669 9.33398 10.6654 9.63246 10.6654 10.0007V12.6673C10.6654 13.0355 10.3669 13.334 9.9987 13.334H9.33203Z'
                                fill='currentColor'
                              />
                              <path
                                fill-rule='evenodd'
                                clip-rule='evenodd'
                                d='M1.33203 2C1.33203 0.895431 2.22746 0 3.33203 0H14.2748L18.6654 4.39052V18C18.6654 19.1045 17.7699 20 16.6654 20H3.33203C2.22746 20 1.33203 19.1045 1.33203 18V2ZM4.66536 8H2.66536V14.6667H3.9987V12H4.66536C5.76994 12 6.66536 11.1046 6.66536 10C6.66536 8.89543 5.76994 8 4.66536 8ZM9.9987 8H7.9987V14.6667H9.9987C11.1033 14.6667 11.9987 13.7712 11.9987 12.6667V10C11.9987 8.89543 11.1033 8 9.9987 8ZM13.332 14.6667V8H17.332V9.33333H14.6654V10.6667H15.9987V12H14.6654V14.6667H13.332Z'
                                fill='currentColor'
                              />
                            </g>
                            <defs>
                              <clipPath id='clip0_16599_21131'>
                                <rect width='20' height='20' fill='white' />
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      ) : uploadedPreviews[fileName] ? (
                        <img
                          className='border-radius-5'
                          src={uploadedPreviews[fileName]}
                          alt='passport img'
                          style={{
                            width: '100%',
                            height: '240px',
                            aspectRatio: '1.54',
                            objectFit: 'cover',
                            objectPosition: 'center top',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#f8f9fa',
                            cursor: 'pointer',
                          }}
                          onClick={() => handlePdfClick(fileName)}
                          title='Click to view image in new tab'
                        />
                      ) : (
                        <ImagePlacholder />
                      )}
                      <div
                        className='position-absolute top-12px right-12px'
                        onClick={() => handleDeleteIconClick(fileName)}
                        style={{ cursor: 'pointer' }}>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='24'
                          height='24'
                          viewBox='0 0 24 24'
                          fill='none'>
                          <rect
                            x='24'
                            y='24'
                            width='24'
                            height='24'
                            rx='12'
                            transform='rotate(-180 24 24)'
                            fill='#EBEBEB'
                          />
                          <path
                            d='M17 7L7 17'
                            stroke='black'
                            strokeLinecap='round'
                          />
                          <path
                            d='M17 17L7 7'
                            stroke='black'
                            strokeLinecap='round'
                          />
                        </svg>
                      </div>
                      <div
                        className='s8 mt-3 cursor-pointer'
                        onClick={() => handlePdfClick(fileName)}>
                        {fileName.length > 30
                          ? fileName.slice(0, 30) + '...'
                          : fileName}
                      </div>
                    </div>
                  </div>
                )
              } else {
                return null
              }
            })}
          </div>

          <div className='mt-3'>
            <label className='s7 mb-2'>Upload Passport</label>
            <form
              id='fileUploadForm'
              className={classNames(
                'upload-zone py-3 text-center position-relative cursor-pointer',
                {
                  'drag-active': dragActive,
                },
              )}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={onButtonClick}>
              <input
                ref={inputRef}
                type='file'
                id='fileUploadInput'
                className='d-none'
                accept='image/jpeg,image/png,application/pdf'
                multiple
                onChange={handleChange}
                disabled={selectedFiles.some((f) => f.status === 'uploading')}
              />
              <div className='mb-3'>
                <UploadIcon />
              </div>
              <p className='s7 mb-2'>
                Choose a file{' '}
                <span className='opacity-50 cursor-pointer'>or</span> drag it
                here
              </p>
              <div className='s9 opacity-50'>
                Supported Format: PDF, JPG, PNG
              </div>
            </form>
          </div>

          {/* Delete confirmation modal */}
          <UnsavedDocumentModal
            showModal={showDeleteModal}
            handleClose={handleDeleteCancel}
            handleLeave={handleDeleteConfirm}
            modalTitle='Delete File'
            modalBody='Are you sure you want to delete this file? This action cannot be undone.'
            cancelLabel='Cancel'
            confirmLabel='Delete'
          />
        </>
      )}
    </div>
  )
}

export default UploadPassport
